'use client';
import { EditorContent, type Extension } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { useEditorWithExtensions } from '@/app/hooks/editor';
import type { TextPromptWithInputItem } from '@/app/types';
import { cn } from '@/app/utils';
import type { RichTextareaRef } from '../../../_types';
import { CustomAgentModel } from './extensions/CustomAgentModel';

interface RichTextareaProps {
  className?: string;
  editorClassName?: string;
  textPrompt: TextPromptWithInputItem;
  /** 占位符 */
  placeholder?: string;
  /** 是否自动聚焦 */
  autoFocus?: boolean;
  /** 占位符类名 */
  placeholderClassName?: string;
  updateTextPrompt: (value: TextPromptWithInputItem) => void;
  disabled?: boolean;
}

// CustomAgentModel扩展的配置
const CustomAgentModelExtension = CustomAgentModel.configure({
  deleteTriggerWithBackspace: true,
}) as Extension;

export const RichTextarea = forwardRef<RichTextareaRef, RichTextareaProps>(
  (
    {
      className,
      editorClassName,
      textPrompt,
      placeholder,
      disabled,
      autoFocus = true,
      placeholderClassName,
      updateTextPrompt,
    },
    ref,
  ) => {
    const t = useTranslations();

    const { editor } = useEditorWithExtensions({
      content: textPrompt,
      updateContent: updateTextPrompt,
      maxLength: MAX_PROMPT_LENGTH,
      onMaxLengthExceeded: () => {
        toast({
          type: 'warning',
          message: t('create.createForm.error.invalidLength', { limit: MAX_PROMPT_LENGTH }),
        });
      },
      extensions: [CustomAgentModelExtension],
      editorProps: {
        attributes: {
          class: cn('h-full outline-none', editorClassName),
        },
      },
    });

    // 暴露方法给父组件
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (editor && !editor.isDestroyed) {
            editor.commands.focus();
          }
        },
        enter: () => {
          if (editor && !editor.isDestroyed) {
            editor.commands.enter();
          }
        },
        getEditor: () => editor,
      }),
      [editor],
    );

    // 自动聚焦
    useEffect(() => {
      if (autoFocus && editor && !editor.isDestroyed && !disabled) {
        editor.commands.focus();
      }
    }, [autoFocus, editor, disabled]);

    return (
      <div className={cn('relative flex h-full w-full flex-col', className)}>
        <div className="absolute inset-0 bottom-6 overflow-auto">
          <EditorContent
            editor={editor}
            className={cn(
              'size-full rounded-none border-none bg-transparent p-0 text-sm text-system-white leading-[1.4] caret-system-white focus-visible:ring-0 focus-visible:ring-offset-0',
              editorClassName,
            )}
          />
        </div>

        {/* 占位符 */}
        {textPrompt.length === 0 && placeholder && (
          <div
            className={cn(
              'pointer-events-none absolute inset-0 select-none items-center text-sm text-system-white36 leading-[1.4]',
              placeholderClassName,
            )}
          >
            {placeholder}
          </div>
        )}
      </div>
    );
  },
);
